export type DraftRole = 'Lead' | 'Trail'

export type DraftingCaseInfo = {
  gmId: string
  role: DraftRole
  partnerGmId: string
  offsetXm: number | null
  offsetYm: number | null
}

export const DRAFT_ROLE_FIELD = 'draft_role'
export const DRAFT_PAIR_INDEX_FIELD = 'draft_pair_index'

/**
 * Badge palette. Colour identifies the pair, the label identifies the role, so a run holding
 * several pairs stays readable. Index cycles once a run exceeds the palette length.
 */
export const DRAFT_PAIR_BADGE_CLASSES = [
  'bg-emerald-600 text-white',
  'bg-blue-600 text-white',
  'bg-amber-600 text-white',
  'bg-violet-600 text-white',
  'bg-rose-600 text-white',
  'bg-teal-600 text-white'
]

export const getDraftPairBadgeClass = (pairIndex: number) =>
  DRAFT_PAIR_BADGE_CLASSES[
    (((pairIndex - 1) % DRAFT_PAIR_BADGE_CLASSES.length) + DRAFT_PAIR_BADGE_CLASSES.length) %
      DRAFT_PAIR_BADGE_CLASSES.length
  ]

/**
 * Tag headline rows that take part in a drafting pair with their role and a per-run pair number.
 * Rows with no drafting info are returned by reference and gain no new keys, so every downstream
 * consumer of non-drafting rows behaves exactly as before.
 */
export const annotateDraftingPairs = (rows: any[], draftingInfo: DraftingCaseInfo[]): any[] => {
  if (!rows?.length || !draftingInfo?.length) return rows

  const infoByGmId = new Map<string, DraftingCaseInfo>()
  draftingInfo.forEach(info => {
    if (info?.gmId) infoByGmId.set(String(info.gmId), info)
  })
  if (!infoByGmId.size) return rows

  const pairIndexByKey = new Map<string, number>()
  const nextPairIndexByRun = new Map<string, number>()

  return rows.map(row => {
    const info = infoByGmId.get(String(row?.gm_id))
    if (!info) return row

    const gmId = String(row.gm_id)
    // Orphan (partner outside the loaded set) keys on itself so it still gets its own colour.
    const pairMembers = info.partnerGmId ? [gmId, String(info.partnerGmId)].sort() : [gmId]
    const runKey = `${row.project}::${row.run}`
    const pairKey = `${runKey}::${pairMembers.join('-')}`

    let pairIndex = pairIndexByKey.get(pairKey)
    if (pairIndex === undefined) {
      pairIndex = nextPairIndexByRun.get(runKey) ?? 1
      pairIndexByKey.set(pairKey, pairIndex)
      nextPairIndexByRun.set(runKey, pairIndex + 1)
    }

    return {
      ...row,
      [DRAFT_ROLE_FIELD]: info.role,
      [DRAFT_PAIR_INDEX_FIELD]: pairIndex
    }
  })
}
